import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap } from 'react-leaflet';
import { LatLngBounds, LatLng, Icon } from 'leaflet';
import { Card, Alert, Spin } from 'antd';
import { ErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';
import { 
  wktToGeoJSON, 
  getPolygonCenter, 
  DEFAULT_MAP_CENTER, 
  DEFAULT_MAP_ZOOM, 
  MAP_TILE_LAYER, 
  POLYGON_STYLE,
  parseCoordinatePair
} from 'common/geoUtils';
import { IMapPolygonViewerProps, IMapError } from 'common/interface/IMapComponent';
import { variables } from 'common/variables';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with webpack
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icon configuration
const createCustomMarkerIcon = () => new Icon({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Styled Components following the existing patterns
const MapWrapper = styled.div`
  height: ${(props: { height: number }) => props.height}px;
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  
  .leaflet-container {
    height: 100%;
    width: 100%;
  }
  
  .leaflet-control-zoom {
    border: none;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .leaflet-control-zoom a {
    background-color: ${variables?.primaryColor || '#1890ff'};
    color: white;
    border: none;
    
    &:hover {
      background-color: ${variables?.primaryColor || '#1890ff'};
      opacity: 0.8;
    }
  }
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background: white;
  padding: 1rem;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ErrorWrapper = styled.div`
  margin-bottom: 1rem;
`;

// Component to handle map bounds fitting for both polygons and markers
const FitBounds: React.FC<{ 
  coordinates?: number[][][]; 
  markerPosition?: [number, number];
  onBoundsSet?: () => void;
}> = ({ coordinates, markerPosition, onBoundsSet }) => {
  const map = useMap();

  useEffect(() => {
    try {
      const bounds = new LatLngBounds([]);

      // Add polygon bounds if available
      if (coordinates && coordinates[0] && coordinates[0].length > 0) {
        coordinates[0].forEach(coord => {
          bounds.extend(new LatLng(coord[1], coord[0]));
        });
      }

      // Add marker bounds if available
      if (markerPosition) {
        bounds.extend(new LatLng(markerPosition[0], markerPosition[1]));
      }

      // Only fit bounds if we have valid bounds
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [20, 20] });
        onBoundsSet?.();
      }
    } catch (error) {
      console.error('Error fitting bounds:', error);
    }
  }, [coordinates, markerPosition, map, onBoundsSet]);

  return null;
};

// Error fallback component
const MapErrorFallback: React.FC<{ error: Error }> = ({ error }) => (
  <ErrorWrapper>
    <Alert
      message="Map Rendering Error"
      description={`Unable to render map: ${error.message}`}
      type="error"
      showIcon
    />
  </ErrorWrapper>
);

// Main MapPolygonViewer component
export const MapPolygonViewer: React.FC<IMapPolygonViewerProps> = ({
  wktData,
  markerPosition,
  markerPopup,
  height = 400,
  width = '100%',
  showControls = true,
  showPolygonInfo = false,
  center,
  zoom,
  className,
  loading = false,
  error,
  onMapReady,
  onPolygonClick,
  onMarkerClick
}) => {
  const [geoData, setGeoData] = useState<any>(null);
  const [mapError, setMapError] = useState<IMapError | null>(null);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_MAP_CENTER);
  const [mapZoom, setMapZoom] = useState(zoom || DEFAULT_MAP_ZOOM);
  const [parsedMarkerPosition, setParsedMarkerPosition] = useState<[number, number] | null>(null);
  const [parsedCenter, setParsedCenter] = useState<[number, number] | null>(null);

  // Parse coordinates when they change
  useEffect(() => {
    if (markerPosition) {
      const parsed = parseCoordinatePair(markerPosition[0], markerPosition[1]);
      setParsedMarkerPosition(parsed);
    } else {
      setParsedMarkerPosition(null);
    }
  }, [markerPosition]);

  useEffect(() => {
    if (center) {
      const parsed = parseCoordinatePair(center[0], center[1]);
      setParsedCenter(parsed);
      if (parsed) {
        setMapCenter(parsed);
      }
    } else {
      setParsedCenter(null);
    }
  }, [center]);

  // Parse WKT data
  useEffect(() => {
    if (!wktData || !wktData.trim()) {
      setGeoData(null);
      setMapError(null);
      return;
    }

    try {
      setIsMapLoading(true);
      const parsed = wktToGeoJSON(wktData);
      
      if (parsed) {
        setGeoData(parsed);
        setMapError(null);
        
        // Set map center based on polygon if not provided
        if (!parsedCenter) {
          const polygonCenter = getPolygonCenter(parsed.geometry.coordinates as number[][][]);
          if (polygonCenter) {
            setMapCenter(polygonCenter);
          }
        }
      } else {
        setMapError({
          type: 'INVALID_WKT',
          message: 'Invalid WKT format',
          details: 'Please check your WKT polygon data format'
        });
        setGeoData(null);
      }
    } catch (err) {
      setMapError({
        type: 'PARSING_ERROR',
        message: 'Error parsing WKT data',
        details: err instanceof Error ? err.message : 'Unknown parsing error'
      });
      setGeoData(null);
    } finally {
      setIsMapLoading(false);
    }
  }, [wktData, parsedCenter]);

  // Set map center based on marker if no polygon and no custom center
  useEffect(() => {
    if (!parsedCenter && !geoData && parsedMarkerPosition) {
      setMapCenter(parsedMarkerPosition);
    }
  }, [parsedMarkerPosition, parsedCenter, geoData]);

  // Handle map ready callback
  const handleMapReady = useCallback(() => {
    onMapReady?.();
  }, [onMapReady]);

  // Handle polygon click
  const handlePolygonClick = useCallback(() => {
    if (geoData && onPolygonClick) {
      onPolygonClick(geoData.geometry.coordinates);
    }
  }, [geoData, onPolygonClick]);

  // Handle marker click
  const handleMarkerClick = useCallback(() => {
    if (parsedMarkerPosition && onMarkerClick) {
      onMarkerClick(parsedMarkerPosition);
    }
  }, [parsedMarkerPosition, onMarkerClick]);

  // Render error state
  if (error || mapError) {
    const errorToShow = error || mapError?.message || 'Unknown error';
    return (
      <ErrorWrapper>
        <Alert
          message="Map Error"
          description={errorToShow}
          type="error"
          showIcon
        />
      </ErrorWrapper>
    );
  }

  // Render empty state
  if (!geoData && !parsedMarkerPosition && !loading && !isMapLoading) {
    return (
      <Card title="Map Visualization" size="small">
        <Alert
          message="No Map Data"
          description="Enter coordinates (latitude & longitude)"
          type="info"
          showIcon
        />
      </Card>
    );
  }

  const coordinates = geoData?.geometry.coordinates as number[][][];
  const polygonPositions = coordinates?.[0]?.map(coord => [coord[1], coord[0]]) as [number, number][];

  return (
    <Card 
      title="Map Visualization" 
      size="small" 
      className={className}
    >
      <MapWrapper height={height}>
        {(loading || isMapLoading) && (
          <LoadingWrapper>
            <Spin size="large" />
          </LoadingWrapper>
        )}
        
        <ErrorBoundary FallbackComponent={MapErrorFallback}>
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: width }}
            zoomControl={showControls}
            attributionControl={showControls}
            whenReady={handleMapReady}
          >
            <TileLayer
              url={MAP_TILE_LAYER.url}
              attribution={MAP_TILE_LAYER.attribution}
            />
            
            {/* Render Polygon if available */}
            {geoData && polygonPositions && (
              <Polygon
                positions={polygonPositions}
                pathOptions={{
                  color: POLYGON_STYLE.color,
                  weight: POLYGON_STYLE.weight,
                  fillColor: POLYGON_STYLE.fillColor,
                  fillOpacity: POLYGON_STYLE.fillOpacity,
                  dashArray: POLYGON_STYLE.dashArray
                }}
                eventHandlers={{
                  click: handlePolygonClick
                }}
              />
            )}
            
            {/* Render Marker if available */}
            {parsedMarkerPosition && (
              <Marker
                position={parsedMarkerPosition}
                icon={createCustomMarkerIcon()}
                eventHandlers={{
                  click: handleMarkerClick
                }}
              >
                {markerPopup && (
                  <Popup>
                    <div>{markerPopup}</div>
                  </Popup>
                )}
              </Marker>
            )}
            
            {/* Fit bounds for both polygon and marker */}
            <FitBounds 
              coordinates={geoData ? coordinates : undefined}
              markerPosition={parsedMarkerPosition || undefined}
              onBoundsSet={handleMapReady}
            />
          </MapContainer>
        </ErrorBoundary>
      </MapWrapper>
      
      {/* Show info for both polygon and marker */}
      {showPolygonInfo && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
          {geoData && `Polygon with ${coordinates?.[0]?.length || 0} vertices`}
          {geoData && parsedMarkerPosition && ' • '}
          {parsedMarkerPosition && `Marker at [${parsedMarkerPosition[0]}, ${parsedMarkerPosition[1]}]`}
        </div>
      )}
    </Card>
  );
}; 